import { z } from 'zod';

/**
 * Who is looking, as far as the visibility engine is concerned
 * (`03-visibility-policy.md`).
 *
 * A closed pair, deliberately: `matched` means an **active** match exists
 * between viewer and owner; **everything else** — nominations, likes lists,
 * search, a match that was unmatched or blocked — is `public`. There is no
 * third context and no "admin" member here, because staff bypass the resolver
 * by permission (N18's only exception) rather than by holding a wider context.
 *
 * `matched` is a **superset surface**: whatever `public` shows, `matched` shows
 * too, plus the fields the owner opened up after acceptance. The engine does
 * not enforce that — an operator may configure a stricter matched policy, which
 * the admin builder flags as almost certainly a mistake (P10) — but it is the
 * shape every member-facing payload is designed around.
 *
 * The context is computed **server-side**, never sent by a client: it is the
 * answer to "does an active match exist", and a client that could name its own
 * context could name `matched`.
 */
export const VIEWER_CONTEXTS = ['public', 'matched'] as const;

export const ViewerContextSchema = z.enum(VIEWER_CONTEXTS);

export type ViewerContext = (typeof VIEWER_CONTEXTS)[number];

const CONTEXT_SET: ReadonlySet<string> = new Set<string>(VIEWER_CONTEXTS);

/** Narrows a plain string — what a stored preference row goes through on read. */
export function isViewerContext(value: string): value is ViewerContext {
  return CONTEXT_SET.has(value);
}

/**
 * The **narrower** of a resolved context and a ceiling placed on it.
 *
 * Lives here, beside the pair itself, because it is a fact about the lattice
 * rather than about any one caller: `matched` is the superset surface, so
 * narrowing is `public` whenever either side says `public`. Written once so a
 * second caller cannot invent the opposite convention — the failure that would
 * produce is a "ceiling" that silently *widens*, which is the one direction this
 * function exists to make impossible (P19a: a scope that changes disclosure must
 * intersect, never skip).
 *
 * Used where a payload is composed for somebody who is **not** the viewer whose
 * eyes resolved it — a guardian reading his ward's conversations (P24b1), where
 * `guardian.chatCounterpartyContext` is the ceiling. It can only ever remove
 * fields: capping at `matched` is a no-op, and capping an unmatched pair at
 * `matched` still yields `public`.
 */
export function narrowViewerContext(
  resolved: ViewerContext,
  ceiling: ViewerContext,
): ViewerContext {
  return resolved === 'matched' && ceiling === 'matched' ? 'matched' : 'public';
}
